import React, { useCallback } from 'react';

import DropdownInput from 'components/form/DropdownInput.component';
import CalendarInput from 'components/generic/CalendarInput.component';
import WidgetTitle from 'components/widget/WidgetTitle.component';
import useResettingState from 'hooks/useResettingState.hook';
import { useTranslation } from 'providers/GILT.provider';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import { fromAnyDateToIso } from 'utils/converters/time/from.util';
import { combineDateTimeIso } from 'utils/date/combine.util';
import correctIsoForDst from 'utils/date/correctIsoForDst.util';
import detectDst from 'utils/date/detectDst.util';
import { formatHourAndMinute } from 'utils/date/format.util';
import { timeAdd60min, timeSub60min } from 'utils/date/manipulate.util';
import call from 'utils/functions/call.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import withOptions from 'utils/functions/withOptions.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import isMissing from 'utils/predicates/any/isMissing.util';

import CN from './WidgetBlockDuration.module.scss';

const L = logger('WidgetBlockDuration');

// NOTE: this is a guesswork to figure out if day changed hours due to DST and manually add/subtract an hour to the time
// SEE: https://getslick.atlassian.net/browse/GS-12019?focusedCommentId=16429
const combineAndCorrect = withOptions(({ date, time }) => correctIsoForDst(combineDateTimeIso({ date, time })));

const recalculate = (options) => {
  const ors = options?.result ?? {};

  const bgnForm = options?.result?.from ?? {};
  const endForm = options?.result?.to ?? {};

  const day = fromAnyDateToIso(ors.day);
  const bgnReconciled = fromAnyDateToIso(bgnForm.value);
  const endReconciled = fromAnyDateToIso(endForm.value);
  const bgnOriginal = fromAnyDateToIso(options?.fields?.from?.value);
  const endOriginal = fromAnyDateToIso(options?.fields?.to?.value);

  if (bgnReconciled !== bgnOriginal && bgnReconciled >= endReconciled) {
    const value = timeAdd60min(bgnForm.value);
    const result = { ...ors, day, to: { value: fromAnyDateToIso(value), description: formatHourAndMinute(value) } };
    return L.debug('recalculate()', 'result:', result);
  }

  if (endReconciled !== endOriginal && endReconciled <= bgnReconciled) {
    const value = timeSub60min(endForm.value);
    const result = { ...ors, day, from: { value: fromAnyDateToIso(value), description: formatHourAndMinute(value) } };
    return L.debug('recalculate()', 'result:', result);
  }

  return L.debug('recalculate()', 'result:', { ...ors, day });
};

const reconcile = (options) => {
  const { key, raw } = options ?? {};
  const fields = asObject(options?.fields ?? {});

  if (key === 'from' || key === 'to') {
    const date = fromAnyDateToIso(fields.day);
    const dst = detectDst(date);
    const time = fromAnyDateToIso(raw?.value);

    const value = combineAndCorrect({ date, time });
    const result = { ...fields, [key]: { ...raw, value } };

    L.debug('reconcile()', key, result, '<-', date, '+', time, '|', dst);
    return recalculate({ fields, result });
  }

  if (key === 'day') {
    const date = fromAnyDateToIso(raw);
    const from = fields.from ?? {};
    const to = fields.to ?? {};

    const result = {
      ...fields,
      [key]: raw,
      from: { ...from, value: combineAndCorrect({ date, time: from.value }) },
      to: { ...to, value: combineAndCorrect({ date, time: to.value }) },
    };

    L.debug('reconcile()', key, result, '<-', date, '+', { from, to });
    return recalculate({ fields, result });
  }

  const result = { ...fields, [key]: raw };

  L.debug('reconcile()', key, result);
  return recalculate({ fields, result });
};

const WidgetBlockDuration = ({ disabled, name: nameFromProps, onChange = alwaysNoop, options: optionsFromProps, selected, tag: tagFromProps }) => {
  const { t } = useTranslation();
  const name = fromAnyToTrimmed(nameFromProps);
  const tag = fromAnyToTrimmed(tagFromProps);

  const [fields, setFields] = useResettingState(selected);

  const handleChange = useCallback(
    (ev) => {
      const raw = reconcile({ key: ev.name, fields, raw: ev.raw });
      const { value } = asObject(call(`WidgetBlockDuration.onChange`, onChange, { name, tag, raw }));
      setFields(isMissing(value) ? raw : value);
    },
    [fields, name, onChange, setFields, tag],
  );

  return (
    <div data-bem="WidgetBlockDuration" data-tag={tag} className={CN.component}>
      <div className={CN.titleContainer}>
        <WidgetTitle tag="WidgetBlockDuration__title">{t('scheduler.timeOff.startsTitle')}</WidgetTitle>
      </div>
      <div data-bem="WidgetBlockDuration__body" className={CN.row}>
        <div className={CN.col}>
          <CalendarInput tag={`${tag}--day`} disabled={disabled} name="day" onChange={handleChange} value={fields.day} />
        </div>
        <div className={CN.col}>
          <div className={CN.dropdowns}>
            <DropdownInput disabled={disabled} name="from" onChange={handleChange} options={optionsFromProps} tag={`${tag}--from`} value={fields.from} />
            <span className={CN.divider}>-</span>
            <DropdownInput align="right" disabled={disabled} name="to" onChange={handleChange} options={optionsFromProps} tag={`${tag}--to`} value={fields.to} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default WidgetBlockDuration;
